package com.iptvplayer.web.ui.screen

import androidx.compose.runtime.*
import com.iptvplayer.shared.domain.model.Episode
import com.iptvplayer.shared.domain.model.SeriesInfo
import com.iptvplayer.shared.domain.repository.ContentRepository
import com.iptvplayer.shared.util.Result
import com.iptvplayer.web.ui.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun SeriesDetailScreen(
    sourceId: String,
    seriesId: Int,
    seriesName: String,
    onPlayEpisode: OnPlayEpisode,
    onBack: () -> Unit,
) {
    val repo = remember { koinGet<ContentRepository>() }
    var info by remember { mutableStateOf<SeriesInfo?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var openSeason by remember { mutableStateOf<Int?>(null) }

    LaunchedEffect(sourceId, seriesId) {
        loading = true; error = null
        when (val r = repo.loadSeriesInfo(sourceId, seriesId)) {
            is Result.Success -> {
                info = r.data
                openSeason = r.data.seasons.firstOrNull()?.seasonNumber
            }
            is Result.Error -> error = r.message
            is Result.Loading -> {}
        }
        loading = false
    }

    PageContainer {
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "12px"); property("margin-bottom", "10px") } }) {
            GhostButton("← Back") { onBack() }
            H2(attrs = { style { property("margin", "0") } }) { Text(seriesName) }
        }
        ErrorText(error)

        when {
            loading -> Spinner("Loading series…")
            info == null -> EmptyState("No series detail available.")
            else -> {
                val i = info!!
                // Header: poster + meta + plot
                Div(attrs = {
                    style {
                        cardStyle(); property("padding", "16px"); display(DisplayStyle.Flex)
                        property("gap", "18px"); property("flex-wrap", "wrap"); property("margin-bottom", "14px")
                    }
                }) {
                    val poster = proxiedOrNull(i.series.posterUrl)
                    if (poster != null) {
                        Img(src = poster, attrs = {
                            attr("loading", "lazy"); attr("decoding", "async")
                            style { property("width", "160px"); property("height", "auto"); property("border-radius", "12px"); property("flex-shrink", "0") }
                        })
                    }
                    Div(attrs = { style { property("flex-grow", "1"); property("min-width", "240px") } }) {
                        val meta = listOfNotNull(
                            i.series.genre,
                            i.series.releaseDate?.take(4),
                            i.series.rating?.let { "★ ${(it * 10).toInt() / 10.0}" }
                        ).joinToString("  ·  ")
                        if (meta.isNotBlank()) {
                            Div(attrs = { style { property("color", Theme.textDim); property("font-size", "13px"); property("margin-bottom", "6px") } }) { Text(meta) }
                        }
                        if (!i.series.plot.isNullOrBlank()) {
                            P(attrs = { style { property("margin", "0"); property("color", Theme.text); property("line-height", "1.55") } }) { Text(i.series.plot ?: "") }
                        }
                    }
                }

                // Season tabs
                if (i.seasons.isEmpty()) {
                    EmptyState("No seasons / episodes available from the provider.")
                } else {
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); property("gap", "8px"); property("flex-wrap", "wrap")
                            property("margin-bottom", "12px")
                        }
                    }) {
                        i.seasons.forEach { s ->
                            Chip("Season ${s.seasonNumber} (${s.episodes.size})", s.seasonNumber == openSeason) {
                                openSeason = s.seasonNumber
                            }
                        }
                    }

                    val current = i.seasons.firstOrNull { it.seasonNumber == openSeason } ?: i.seasons.first()
                    if (current.episodes.isEmpty()) {
                        EmptyState("No episodes for this season.")
                    } else {
                        current.episodes.forEach { ep ->
                            EpisodeRow(ep) {
                                // Pass the series context so the player can auto-advance episodes.
                                onPlayEpisode(ep.id, "$seriesName · ${epLabel(ep)}", ep.streamUrl, ep.coverUrl, sourceId, seriesId)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun EpisodeRow(ep: Episode, onClick: () -> Unit) {
    val durationLabel = ep.durationSec?.let { secs ->
        val m = (secs / 60).toInt()
        if (m >= 60) "${m / 60}h ${m % 60}m" else "${m}m"
    }
    val subtitle = listOfNotNull(durationLabel, ep.plot?.take(160)).joinToString("  ·  ").ifBlank { null }
    RowItem(
        name = epLabel(ep),
        imageUrl = ep.coverUrl,
        subtitle = subtitle,
        onClick = onClick,
        actions = { GhostButton("▶  Play") { onClick() } },
    )
}

private fun epLabel(ep: Episode): String =
    "S${ep.seasonNumber.toString().padStart(2, '0')}E${ep.episodeNumber.toString().padStart(2, '0')}  ${ep.title}"
